import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  List, 
  ListItemButton,
  ListItemText, 
  ListItemIcon,
  Collapse,
  Box,
  alpha,
  useTheme
} from '@mui/material';
import HouseIcon from '@mui/icons-material/House';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GroupsIcon from '@mui/icons-material/Groups';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CategoryIcon from '@mui/icons-material/Category';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BugReportIcon from '@mui/icons-material/BugReport';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BlockIcon from '@mui/icons-material/Block';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CasinoIcon from '@mui/icons-material/Casino';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { authAPI } from '../../Api';

export function SidebarMenu() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const [openSubMenus, setOpenSubMenus] = useState({});
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchRole() {
            try {
                const res = await authAPI.checkToken();
                setUserRole(res.data?.data?.role || 'user');
            } catch (e) {
                setUserRole('user');
            } finally {
                setLoading(false);
            }
        }
        fetchRole();
    }, []);

    useEffect(() => {
        if (location.pathname.startsWith('/v4')) {
            setOpenSubMenus(previous => previous['/v4'] === undefined ? { ...previous, '/v4': true } : previous);
        }
    }, [location.pathname]);

    if (loading) return null;

    const menuItems = [
        { icon: <HouseIcon />, text: 'Главная', path: '/', roles: ['admin', 'operator','ownershop'] },
        { icon: <ConfirmationNumberIcon />, text: 'Тикеты', path: '/tickets', roles: ['admin', 'operator'] },
        { icon: <MonetizationOnIcon />, text: 'Учёт', path: '/accounting/list', roles: ['admin'] },
        { icon: <MonetizationOnIcon />, text: 'Выплаты', path: '/withdrawals/list', roles: ['admin'] },
        { icon: <AssessmentIcon />, text: 'Отчёт по прибыли', path: '/profit-report', roles: ['admin'] },
        { icon: <CardGiftcardIcon />, text: 'Подарочные карты', path: '/giftcards', roles: ['admin'] },
        { icon: <CampaignIcon />, text: 'Рассылки', path: '/broadcasts', roles: ['admin'] },
        { 
            icon: <SmartToyIcon />, 
            text: 'TG Аккаунты', 
            path: '/tg-accounts',
                roles: ['admin'],
                subMenu: [
                  { icon: <PeopleIcon />, text: 'Список аккаунтов', path: '/tg-accounts/list', roles: ['admin'] },
                  { icon: <PersonAddIcon />, text: 'Добавить аккаунт', path: '/tg-accounts/add', roles: ['admin'] },
                ]
        },
        { icon: <ShoppingCartIcon />, text: 'Товары', path: '/goods', roles: ['admin', 'operator'], subMenu: [
            { icon: <AddShoppingCartIcon />, text: 'Создать товар', path: '/goods/add', roles: ['admin', 'operator'] },
            { icon: <ShoppingCartIcon />, text: 'Список товаров', path: '/goods/list', roles: ['admin', 'operator'] },
        ] },
        { icon: <PeopleIcon />, text: 'TG Пользователи', path: '/tgusers', roles: ['admin'] },
        { 
          icon: <AccountBalanceWalletIcon />, 
          text: 'Crypto', 
          path: '/crypto',
          roles: ['admin'],
          subMenu: [
            { icon: <AccountBalanceWalletIcon />, text: 'Реквизиты', path: '/crypto/wallets', roles: ['admin'] },
          ]
        },  
        { 
          icon: <PeopleIcon />, 
          text: 'Пользователи', 
          path: '/users',
          roles: ['admin'],
          subMenu: [
            { icon: <PersonAddIcon />, text: 'Создать пользователя', path: '/users/add', roles: ['admin'] },
            { icon: <PeopleIcon />, text: 'Список пользователей', path: '/users/list', roles: ['admin'] },
          ]
        },
        { 
            icon: <PeopleIcon />, 
            text: 'Мерчанты', 
            path: '/merchants',
            roles: ['admin', 'manager'],
            subMenu: [
              { icon: <PersonAddIcon />, text: 'Создать мерчанта', path: '/merchants/add', roles: ['admin'] },
              { icon: <PeopleIcon />, text: 'Список мерчантов', path: '/merchants/list', roles: ['admin'] },
            ]
          },
        { 
            icon: <PeopleIcon />, 
            text: 'Клиенты', 
            path: '/clients',
            roles: ['admin','operator'],
            subMenu: [
              { icon: <PersonAddIcon />, text: 'Создать клиента', path: '/clients/add', roles: ['admin', 'operator'] },
              { icon: <PeopleIcon />, text: 'Список клиентов', path: '/clients/list', roles: ['admin', 'operator'] },
            ]
          },
        { 
            icon: <PeopleIcon />, 
            text: 'Партнеры', 
            path: '/partners',
            roles: ['admin', 'operator'],
            subMenu: [
              { icon: <PersonAddIcon />, text: 'Добавить партнера', path: '/partners/add', roles: ['admin', 'operator'] },
              { icon: <PeopleIcon />, text: 'Список партнеров', path: '/partners/list', roles: ['admin', 'operator'] },
            ]
          },
        { icon: <ShoppingBasketIcon />, text: 'Ордера', path: '/orders/list', roles: ['admin'] },
        {
            icon: <LayersOutlinedIcon />,
            text: 'V4',
            path: '/v4',
            roles: ['admin'],
            subMenu: [
                { icon: <AssessmentIcon />, text: 'Обзор', path: '/v4', roles: ['admin'] },
                { icon: <ShoppingBasketIcon />, text: 'Ордера', path: '/v4/orders', roles: ['admin'] },
                { icon: <PeopleIcon />, text: 'Мерчанты', path: '/v4/merchants', roles: ['admin'] },
                { icon: <PeopleIcon />, text: 'Telegram-пользователи', path: '/v4/telegram-users', roles: ['admin'] },
                { icon: <PeopleIcon />, text: 'Балансы сайтов', path: '/v4/site-balances', roles: ['admin'] },
                { icon: <AccountBalanceWalletIcon />, text: 'Выплаты мерчантам', path: '/v4/payouts', roles: ['admin'] },
                { icon: <AccountBalanceWalletIcon />, text: 'Долги процессингов', path: '/v4/processing-debts', roles: ['admin'] },
                { icon: <AccountBalanceWalletIcon />, text: 'Telegram-бот', path: '/v4/telegram-bot', roles: ['admin'] },
                { icon: <AccountBalanceWalletIcon />, text: 'Криптоадреса', path: '/v4/crypto-addresses', roles: ['admin'] },
                { icon: <CategoryIcon />, text: 'Процессинги', path: '/v4/processings', roles: ['admin'] },
                { icon: <GroupsIcon />, text: 'Маршруты', path: '/v4/routes', roles: ['admin'] },
                { icon: <MonetizationOnIcon />, text: 'Тарифы', path: '/v4/tariffs', roles: ['admin'] },
                { icon: <CampaignIcon />, text: 'Очереди', path: '/v4/queues', roles: ['admin'] },
                { icon: <VpnKeyIcon />, text: 'Секреты', path: '/v4/secrets', roles: ['admin'] },
                { icon: <SettingsIcon />, text: 'Система', path: '/v4/system', roles: ['admin'] },
            ],
        },
        { icon: <ShoppingBasketIcon />, text: 'Баг трекер', path: '/bugtr', roles: ['admin'] },
        { icon: <BlockIcon />, text: 'Черный список', path: '/blacklist', roles: ['admin'] },
        {
            icon: <CasinoIcon />,
            text: 'Лудки',
            path: '/games',
            roles: ['admin'],
            subMenu: [
              { icon: <CasinoIcon />, text: 'Игры', path: '/games/list', roles: ['admin'] },
              { icon: <AssessmentIcon />, text: 'История игр', path: '/games/history', roles: ['admin'] },
            ]
        },
        { icon: <ShoppingBasketIcon />, text: 'Активные заявки FunPay', path: '/orders/funpay', roles: ['admin', 'operator'] },
        { 
            icon: <SettingsIcon />, 
            text: 'Платежные методы', 
            path: '/processing',
            roles: ['admin'],
            subMenu: [
              { icon: <CategoryIcon />, text: 'Список процессингов', path: '/processing/list', roles: ['admin'] },
              { icon: <GroupsIcon />, text: 'Группы процессингов', path: '/processing/groups', roles: ['admin'] },
              { icon: <SettingsIcon />, text: 'Группы процев 2', path: '/processing/procgroups2', roles: ['admin'] },
            ]
        },
    ];

    // Функция для переключения состояния подменю
    const handleToggleSubMenu = (path) => {
        setOpenSubMenus(prevState => ({
            ...prevState,
            [path]: !prevState[path] // Переключаем состояние подменю по ключу (пути)
        }));
    };

    // Фильтрация по ролям
    function filterByRole(items) {
        return items
            .filter(item => !item.roles || item.roles.includes(userRole))
            .map(item => ({
                ...item,
                subMenu: item.subMenu ? filterByRole(item.subMenu) : undefined
            }));
    }
    const filteredMenu = filterByRole(menuItems);

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    return (
        <List sx={{ px: 2, py: 1 }}>
            {filteredMenu.map((item, index) => {
                const active = isActive(item.path);
                return (
                    <Box key={index} sx={{ mb: 0.5 }}>
                        <ListItemButton
                            component="li"
                            onClick={() => {
                                if (item.subMenu) {
                                    handleToggleSubMenu(item.path);
                                } else {
                                    navigate(item.path);
                                }
                            }}
                            sx={{
                                borderRadius: 2,
                                mb: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                background: active 
                                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                                    : 'transparent',
                                backdropFilter: active ? 'blur(10px)' : 'none',
                                border: active ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid transparent',
                                '&:hover': {
                                    backgroundColor: alpha('#667eea', 0.15),
                                    transform: 'translateX(4px)',
                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                },
                                py: 1.2,
                                px: 2,
                            }}
                        >
                            <ListItemIcon 
                                sx={{ 
                                    minWidth: 40,
                                    color: active ? '#818cf8' : '#94a3b8',
                                    transition: 'color 0.2s ease',
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontSize: '0.9rem',
                                    fontWeight: active ? 600 : 500,
                                    color: active ? '#f1f5f9' : '#cbd5e1',
                                }}
                            />
                            {item.subMenu && (
                                openSubMenus[item.path] 
                                    ? <ExpandLessIcon sx={{ color: active ? '#818cf8' : '#94a3b8' }} /> 
                                    : <ExpandMoreIcon sx={{ color: active ? '#818cf8' : '#94a3b8' }} />
                            )}
                        </ListItemButton>

                        {item.subMenu && (
                            <Collapse in={openSubMenus[item.path]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding sx={{ pl: 2 }}>
                                    {item.subMenu.map((subItem, subIndex) => {
                                        const subActive = subItem.path === '/v4' ? location.pathname === '/v4' : isActive(subItem.path);
                                        return (
                                            <ListItemButton
                                                component="li"
                                                key={subIndex}
                                                onClick={() => navigate(subItem.path)}
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                    py: 1,
                                                    px: 2,
                                                    transition: 'all 0.2s ease-in-out',
                                                    background: subActive 
                                                        ? alpha('#667eea', 0.15)
                                                        : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: alpha('#667eea', 0.1),
                                                        transform: 'translateX(4px)',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon 
                                                    sx={{ 
                                                        minWidth: 36,
                                                        color: subActive ? '#818cf8' : '#94a3b8',
                                                    }}
                                                >
                                                    {subItem.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={subItem.text}
                                                    primaryTypographyProps={{
                                                        fontSize: '0.85rem',
                                                        fontWeight: subActive ? 600 : 400,
                                                        color: subActive ? '#f1f5f9' : '#cbd5e1',
                                                    }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        )}
                    </Box>
                );
            })}
        </List>
    );
}

export default SidebarMenu;
